import { Link } from "react-router-dom";
import { SITE } from "@/lib/site";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const WEEKS = [
  {
    n: "01",
    title: "Top-down & bottom-up",
    summary:
      "How the brain and body each shape your stress response — and why working with both is more effective than working with either alone.",
    points: [
      "The basic architecture of your nervous system, plainly explained",
      "Why purely cognitive strategies often plateau",
      "Practical bottom-up entry points you already have access to",
    ],
  },
  {
    n: "02",
    title: "The vagus nerve",
    summary:
      "An evidence-based look at the vagus nerve and what research actually supports.",
    points: [
      "Anatomy, function, and what the science does and does not show",
      "Recognising states of regulation, mobilisation, and shutdown",
      "Skills for shifting state, taught without performance",
    ],
  },
  {
    n: "03",
    title: "Overstimulation",
    summary:
      "Modern life is, structurally, a lot. Learn to read your own thresholds and design days that don’t require quitting everything.",
    points: [
      "Signs of an overstimulated nervous system you may be missing",
      "Sensory, social, and digital load — assessed honestly",
      "Recovery practices that fit a working life",
    ],
  },
  {
    n: "04",
    title: "Saying no, and being okay",
    summary:
      "Boundaries without the lecture. A working framework for declining what depletes you, in language that fits real relationships.",
    points: [
      "Why ‘no’ is a regulation skill, not just a communication one",
      "Phrasing that sounds like you",
      "Living with the discomfort that follows a clear no",
    ],
  },
  {
    n: "05",
    title: "Trigger identification",
    summary:
      "Separate the situation, the sensation, and the story. A clinician’s framework for tracing reactions back to their actual sources.",
    points: [
      "A three-layer model of a triggered moment",
      "Common patterns and what they’re usually pointing at",
      "Self-inquiry questions that don’t spiral into rumination",
    ],
  },
  {
    n: "06",
    title: "Daily self-reflection",
    summary:
      "A small, sustainable practice for noticing what your nervous system is asking for — built to outlast the course itself.",
    points: [
      "A short daily protocol that takes under five minutes",
      "How to integrate the prior five weeks into ordinary days",
      "Closing session: building your own ongoing practice",
    ],
  },
];

const FAQ = [
  {
    q: "What is the format of each session?",
    a: "Each week we meet live online for a structured, education-first session. Expect teaching, examples, and time for practical questions. Sessions are not group therapy.",
  },
  {
    q: "Will I have to share personal details?",
    a: "No. This is an educational program. Participation is welcomed but never required, and sharing is kept at a level appropriate to a learning environment.",
  },
  {
    q: "Is this a substitute for therapy?",
    a: "No. Rewire and Root is educational. If you are in clinical need, please work with a licensed provider in your jurisdiction. See the full disclaimer below.",
  },
  {
    q: "What if I miss a week?",
    a: "Replays of the live teaching portion are made available to enrolled participants for the duration of the cohort.",
  },
];

export default function Curriculum() {
  return (
    <div className="rr-reveal">
      <section className="pt-24 md:pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12">
          <p
            className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
            style={{ color: "var(--rr-primary)" }}
          >
            Six-week curriculum
          </p>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight max-w-3xl"
            style={{ color: "var(--rr-text)" }}
            data-testid="curriculum-title"
          >
            A clear map of what we’ll cover.
          </h1>
          <p
            className="mt-8 max-w-2xl text-lg leading-relaxed"
            style={{ color: "var(--rr-text-2)" }}
          >
            Each week builds on the one before. By the end of the program you’ll have a working framework and a small set of skills you can keep using long after the cohort closes.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12">
          <Accordion
            type="single"
            collapsible
            defaultValue="item-01"
            className="border-t"
            data-testid="curriculum-accordion"
            style={{ borderColor: "var(--rr-border)" }}
          >
            {WEEKS.map((w) => (
              <AccordionItem
                key={w.n}
                value={`item-${w.n}`}
                className="border-b"
                style={{ borderColor: "var(--rr-border)" }}
                data-testid={`week-${w.n}`}
              >
                <AccordionTrigger className="py-7 hover:no-underline">
                  <div className="flex items-baseline gap-6 sm:gap-10 text-left w-full">
                    <span
                      className="font-mono text-xs tracking-[0.18em] shrink-0"
                      style={{ color: "var(--rr-accent)" }}
                    >
                      {w.n}
                    </span>
                    <span
                      className="font-serif text-2xl sm:text-3xl leading-tight"
                      style={{ color: "var(--rr-text)" }}
                    >
                      {w.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-0 sm:pl-[78px] pb-8 grid grid-cols-1 md:grid-cols-12 gap-8">
                    <p
                      className="md:col-span-7 text-base leading-relaxed"
                      style={{ color: "var(--rr-text-2)" }}
                    >
                      {w.summary}
                    </p>
                    <ul
                      className="md:col-span-5 space-y-2 text-sm"
                      style={{ color: "var(--rr-text-2)" }}
                    >
                      {w.points.map((p, i) => (
                        <li key={i} className="flex gap-3">
                          <span style={{ color: "var(--rr-accent)" }}>—</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-24 md:py-32 border-t"
        style={{ borderColor: "var(--rr-border)" }}
        data-testid="faq-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p
              className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
              style={{ color: "var(--rr-primary)" }}
            >
              Common questions
            </p>
            <h2
              className="font-serif text-3xl md:text-4xl leading-[1.08] tracking-tight"
              style={{ color: "var(--rr-text)" }}
            >
              The practical details.
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="border-t"
              style={{ borderColor: "var(--rr-border)" }}
            >
              {FAQ.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-b"
                  style={{ borderColor: "var(--rr-border)" }}
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="py-6 text-left text-base sm:text-lg"
                    style={{ color: "var(--rr-text)" }}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="pb-6 text-sm sm:text-base leading-relaxed"
                    style={{ color: "var(--rr-text-2)" }}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <DisclaimerBanner testid="curriculum-disclaimer-banner" />

      <section className="py-20 md:py-28" style={{ background: "var(--rr-surface)" }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-12">
          <h2
            className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl"
            style={{ color: "var(--rr-text)" }}
          >
            Six weeks. {SITE.priceLabel}. One small group.
          </h2>
          <div className="mt-10">
            <Link
              to="/enroll"
              data-testid="curriculum-enroll-cta"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-medium"
              style={{ background: "var(--rr-primary)", color: "var(--rr-bg)" }}
            >
              Enroll now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
